import React, { useState, useEffect } from 'react';
import { bool, func, object } from 'prop-types';
import moment from 'moment';
import { Drawer, Tabs, Divider, notification } from 'antd';
import { filter, includes, orderBy, map, get, toNumber, find, omit, isEmpty, head, first } from 'lodash';
import { ConfirmFooterButtons, FormInput, CompanyGroupsSelect, FormSelect, Text, Column, Row, Icon } from '../../common';
import UPDATE_POLL from '../../../queries/UPDATE_POLL';

const styleNotification = {
    borderRadius: 8,
    height: 42,
    padding: '9px 12px',
    top: 15
};

const { TabPane } = Tabs;

const DrawerRequired = ({ visible, handleDrawer, session, pollSelected, client, refreshData }) => {
    const sessionSalesChannel = orderBy(map(get(session, 'salesChannels'), ({ name, store, id, kind }) => ({ label: `${store} - ${name}`, value: id, kind })), ['label'], ['asc']);
    const [associatedStores, setAssociatedStores] = useState({});
    const [selectedCompanyGroup, onChangeSelectedCompanyGroup] = useState({});
    const [loading, setLoading] = useState(false);
    const [salesChannel, setSalesChannel] = useState({});
    const [amount, setAmount] = useState({});
    const [currentTab, setCurrentTab] = useState(1);

    const sessionSalesChannelFiltered = filter(sessionSalesChannel, ({ value }) => {
        return includes(map(get(pollSelected, 'associatedStore'), 'idStore'), toNumber(value));
    });

    const optionsStores = () => {
        return isEmpty(selectedCompanyGroup)
            ? sessionSalesChannelFiltered
            : filter(sessionSalesChannelFiltered, ({ kind }) => includes(selectedCompanyGroup, kind));
    };
    useEffect(() => {
        const stores = !isEmpty(associatedStores) ? associatedStores : get(pollSelected, 'associatedStore');

        const selectedDassociatedStore = find(stores, ({ idStore }) => {
            return idStore === toNumber(get(salesChannel, 'value'));
        });

        setAmount(get(selectedDassociatedStore, 'amount'));
    }, [salesChannel]);

    const updatedAssociatedStore = () => {
        const stores = get(pollSelected, 'associatedStore');

        return map(stores, (associatedStore) => {
            const store = omit(associatedStore, '__typename');
            if (isEmpty(salesChannel)) return { ...store, amount: amount || get(pollSelected, 'minPollsAnswered') };

            return get(store, 'idStore') === toNumber(get(salesChannel, 'value'))
                ? { ...store, amount: amount || get(pollSelected, 'minPollsAnswered') }
                : store;
        });
    };

    const updatePoll = async () => {
        setLoading(true);
        const response = await client.mutate({
            mutation: UPDATE_POLL,
            variables: {
                input: {
                    idPoll: get(pollSelected, 'idPoll'),
                    associatedStore: updatedAssociatedStore(),
                    userUpdate: get(session, 'email'),
                    changeRequiredResponse: true,
                    ...((currentTab === 1) && { minPollsAnswered: amount || get(pollSelected, 'minPollsAnswered') })
                }
            }
        });

        const isSuccess = get(response, `data.updatePoll.internalCode`) === '200';
        showNotification(isSuccess);
        setLoading(false);
        refreshData();
    };

    const resetValues = () => {
        setAmount({});
        setSalesChannel({});
        onChangeSelectedCompanyGroup({});
    };

    const closeDrawer = () => {
        resetValues();
        handleDrawer();
    };

    const showNotification = (isSuccess) => {
        const error = {
            msg: 'Hubo un error al editar encuesta',
            color: '#BD2C00',
            icon: 'alert-circle',
            bgColor: '#F7E6E1',
            borderColor: '#BD2C00'
        };

        const success = {
            msg: 'La encuesta se actualizó correctamente.',
            color: '#22A889',
            icon: 'checkmark-circle',
            bgColor: '#E6F5F1',
            borderColor: '#B6DFD6'
        };

        const { msg, bgColor, icon, color, borderColor } = isSuccess ? success : error;

        notification.open({
            message: (<Row align='center'>
                <Icon name={icon} size={24} color={color} />
                <Divider type='vertical' style={{ background: color, height: '1.5em', margin: '0px 16px 0px 14px' }} />
                <Text text={msg} size={12} color='#323A44' />
            </Row>),
            style: { ...styleNotification, backgroundColor: bgColor, border: `1px solid ${borderColor}` }
        });
    };

    const disabled = () => {
        if (currentTab === 1) return false;
        return isEmpty(salesChannel);
    };

    const flag = get(head(sessionSalesChannelFiltered), 'kind');

    return <Drawer
        title='Obligatoriedad'
        placement='right'
        onClose={closeDrawer}
        visible={visible}
        width={400}
        bodyStyle={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}
        destroyOnClose
    >
        <Tabs defaultActiveKey='1' style={{ padding: '0px 0px' }} onChange={(activeKey) => { resetValues(); setCurrentTab(toNumber(activeKey)); }}>
            <TabPane tab='General' key='1' style={{ marginBottom: 105, fontWeight: 'bold' }}>
                <Column>
                    <Text text='Escoge un valor numérico predeterminado para todas las tiendas.' paddingBottom={10} paddingTop={20} />
                    <FormInput
                        label='Valor numérico'
                        onlyNumbers
                        value={amount || get(pollSelected, 'minPollsAnswered')}
                        onChange={({ target }) => setAmount(toNumber(get(target, 'value', '')))}
                    />
                </Column>
            </TabPane>
            <TabPane tab='Perzonalizado' key='2'>
                <Column pB={400}>
                    <Text text='Personaliza el valor numérico para tiendas específicas.' paddingBottom={10} paddingTop={20} />
                    <CompanyGroupsSelect
                        sessionSalesChannel={sessionSalesChannelFiltered}
                        onChange={({ value }) => {
                        onChangeSelectedCompanyGroup(value);
                    }} initialValue={{ label: flag, value: flag }} />
                    <FormSelect
                        label='Tienda'
                        options={optionsStores()}
                        value={salesChannel}
                        onChange={({ value }) => setSalesChannel(value)}
                    />
                    <FormInput
                        label='Valor numérico'
                        onlyNumbers
                        value={amount}
                        onChange={({ target }) => setAmount(toNumber(get(target, 'value', '')))}
                    />
                </Column>
            </TabPane>
        </Tabs>
        <ConfirmFooterButtons justify='center' width={168} withZindex onCancel={closeDrawer} onConfirm={updatePoll} loading={loading} confirmDisabled={disabled() || get(pollSelected, 'numberReponses', 0) > 0} />
    </Drawer>;
};

DrawerRequired.propTypes = {
    visible: bool,
    handleDrawer: func,
    session: object,
    pollSelected: object,
    client: object,
    refreshData: func
};

export default DrawerRequired;
